import {
    useGetThematicMeetingTranscript,
    useUploadThematicMeetingTranscript,
} from '@/api/thematic-meetings';
import { MeetingTranscript, useMeetingTranscriptScreen } from '@/components/MeetingTranscript';
import { useThematicMeetingRecap } from '@/pages/ThematicMeetings/Plan/hooks/useThematicMeetingRecap';

/**
 * What the picker offers, and what the endpoint accepts: the two must say the same thing, or the
 * screen invites a file the server then refuses. `.vtt` is the subtitle export a video call produces,
 * which is where most managers will find their text.
 */
const ACCEPTED_FILE_TYPES = '.txt,.md,.vtt';

/**
 * Mirrors `ThematicMeetingTranscriptController`'s own limit for this endpoint — far below the
 * assessment's, since a thematic meeting's transcript is only ever a text export.
 */
const MAX_FILE_SIZE_MB = 2;

interface ThematicMeetingTranscriptSectionProps {
    meetingId: string;
    dataTestId?: string;
}

/**
 * Thematic-meeting binding of the shared transcript screen: this meeting's transcript, the recap
 * generation around it, and the `thematicMeetings.plan.transcript.*` wording. It sits on the page
 * background, as in the evaluation journey: its own cards are glass, and a card around them would hide them.
 *
 * One download is offered where the evaluation journey offers two: the recap, never the transcript.
 * The manager uploaded the file and still has it, so handing a copy back would only add one more copy
 * of a private conversation to keep track of — but they never had the text the model wrote from it.
 */
export const ThematicMeetingTranscriptSection = ({
    meetingId,
    dataTestId = 'thematic-meeting-transcript',
}: ThematicMeetingTranscriptSectionProps) => {
    const transcriptQuery = useGetThematicMeetingTranscript(meetingId);
    const upload = useUploadThematicMeetingTranscript(meetingId);
    const recapGeneration = useThematicMeetingRecap(
        meetingId,
        transcriptQuery.data?.pendingRecapCorrelationId,
    );
    const screen = useMeetingTranscriptScreen({
        namespace: 'thematicMeetings.plan.transcript',
        transcriptQuery,
        upload,
        recapGeneration,
        maxFileSizeMb: MAX_FILE_SIZE_MB,
    });

    return (
        <MeetingTranscript
            {...screen}
            acceptedFileTypes={ACCEPTED_FILE_TYPES}
            spacing={'section'}
            dataTestId={dataTestId}
        />
    );
};
